import type { ReactNode } from 'react';

import { ratioOf } from './ratio';
import type { BarTone } from './ratio';

/**
 * Proportions du prototype (`.ring` : 186 px de côté, 15 px d'épaisseur),
 * exprimées en fraction pour que toute taille garde le même dessin.
 */
const THICKNESS_RATIO = 15 / 186;

/**
 * Anneau de progression — refonte « Nourri ».
 *
 * L'objet-héros de l'écran du jour : `design-tokens.md` (principe 1) veut
 * qu'il reste **le seul « gros » objet visuel de l'écran**. En mettre deux
 * côte à côte, c'est n'en avoir aucun.
 *
 * 🔴 **En SVG, là où le prototype emploie un `conic-gradient`.** La forme est
 * identique ; le trou, lui, en est vraiment un. Le dégradé conique dessine un
 * disque plein dont le centre est masqué par une pastille `--surface` : posé
 * ailleurs que sur une carte blanche, il montrerait une tache. Le raisonnement
 * complet est dans `styles/nourri.css`.
 *
 * 🔴 **L'arc sature à 100 %, le chiffre non** (`ratioOf`). Et un objectif nul
 * laisse la piste nue plutôt que de la remplir — un repas ajouté à la main n'a
 * pas de cible, `410 / 0` vaudrait l'infini.
 *
 * ⚠️ Les chiffres sont du **HTML posé par-dessus**, jamais du texte SVG : rien
 * ne grossit de travers sur une colonne de bureau, et aucun `ResizeObserver`
 * n'est nécessaire — celui de l'étape 8 avait coûté un défaut.
 */
export function ProgressRing({
  value,
  max,
  tone = 'brand',
  size = 186,
  primary,
  caption,
  secondary,
  label,
}: {
  value: number;
  max: number;
  tone?: BarTone;
  /** Diamètre en pixels. L'épaisseur suit la proportion du prototype. */
  size?: number;
  /** Le gros chiffre au centre — prototype `.rc .big`. */
  primary?: ReactNode;
  /** La capitale sous le chiffre — prototype `.rc .cap`. */
  caption?: string;
  /** Le ratio en pied — prototype `.rc .ratio`. */
  secondary?: ReactNode;
  /**
   * Nom accessible. ⚠️ Nécessaire : le centre est du texte décoratif du point
   * de vue du rôle `progressbar`, qui n'annonce qu'un pourcentage.
   */
  label?: string;
}) {
  const ratio = ratioOf(value, max);

  // Unités du `viewBox`, indépendantes de `size` : le dessin est le même
  // partout, seule l'échelle change.
  const stroke = THICKNESS_RATIO * 100;
  const radius = (100 - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="n-ring" style={{ width: size, height: size }}>
      <svg
        className="n-ring__svg"
        width={size}
        height={size}
        viewBox="0 0 100 100"
        role="progressbar"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <circle
          className="n-ring__track"
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          strokeWidth={stroke}
        />
        {ratio > 0 && (
          <circle
            className={`n-ring__arc n-ring__arc--${tone}`}
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - ratio)}
          />
        )}
      </svg>

      <div className="n-ring__center">
        {primary !== undefined && (
          <div className="n-ring__value" style={{ fontSize: size * 0.226 }}>
            {primary}
          </div>
        )}
        {caption && (
          <div className="n-ring__cap" style={{ fontSize: Math.max(9, size * 0.056) }}>
            {caption}
          </div>
        )}
        {secondary !== undefined && (
          <div className="n-ring__sub" style={{ fontSize: Math.max(10, size * 0.067) }}>
            {secondary}
          </div>
        )}
      </div>
    </div>
  );
}
